import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatMoney, formatQuantity, type Centavos } from '@abastece/shared';
import { precioId, type Producto } from '@abastece/core';
import { db } from '../db/dexie.js';
import {
  asegurarListasBase,
  buscarEntre,
  desactivarProductos,
  lecturaDeCodigo,
  listaPredeterminada,
  precioDe,
  reactivarProductos,
  totalizar,
  unidadesDe,
} from '../lib/catalogo.js';
import { useLectorUsb } from '../lib/lector-usb.js';
import { usePuede } from '../lib/sesion.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { PreguntaCodigo } from '../ui/BuscadorProducto.js';
import { CatalogoDePrecios } from '../ui/CatalogoDePrecios.js';
import { CualEs } from '../ui/CualEs.js';
import { EnsenarCodigo } from '../ui/EnsenarCodigo.js';
import { BotonEscanear } from '../ui/Escaner.js';
import { EscanerProductos } from '../ui/EscanerProductos.js';
import { Chip, Ico } from '../ui/Iconos.js';
import { Miniatura } from '../ui/Miniatura.js';
import { Modal } from '../ui/Modal.js';
import { Confirmar } from '../ui/Preguntar.js';
import { VerMasAlBajar } from '../ui/VerMasAlBajar.js';
import { localeActual, useT } from '../i18n/index.js';
import { etiquetaCategoriaProducto, etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';

/**
 * How many rows the list draws at first, and how many more each time its foot comes near. It used
 * to draw these and stop, at about «C» of a starter catalogue, with no way to the rest (the owner,
 * 2026-10-12).
 */
const LOTE = 60;

export function Productos() {
  const t = useT();
  const nombre = useNombreProducto();
  const navigate = useNavigate();
  const { vista } = useSearch({ from: '/productos' });
  /** The products taken off sale, where they are found again and put back. */
  const desactivados = vista === 'desactivados';
  const [consulta, setConsulta] = useState('');
  /** How many rows are drawn: more as the list is scrolled down, the first part again on a new search. */
  const [cuantos, setCuantos] = useState(LOTE);
  const [escaneando, setEscaneando] = useState(false);
  /**
   * The ones ticked, to take off sale together, or to put back under «Desactivados». `null` is
   * the ordinary list, where a row opens its product. The starter catalogue loads a hundred and
   * twenty at a stroke, and a shop that sells a third of them is not going to open the other
   * eighty one by one.
   */
  const [elegidos, setElegidos] = useState<ReadonlySet<string> | null>(null);
  const eligiendo = elegidos !== null;
  const [confirmando, setConfirmando] = useState(false);
  /** How many of the ticked ones are done, while it works through them. */
  const [avance, setAvance] = useState<number | null>(null);
  /** The search box, where whatever a USB scanner types on this screen is read (`useLectorUsb`). */
  const caja = useRef<HTMLInputElement>(null);
  /** A code scanned that nobody in the shop has, asked about in a sheet. */
  const [pregunta, setPregunta] = useState<string | null>(null);
  /** That code, being given to one of the shop's products. */
  const [ensenando, setEnsenando] = useState<string | null>(null);
  /** A code scanned that more than one product answers to, and which: asked which is in the hand. */
  const [varios, setVarios] = useState<{ codigo: string; productos: Producto[] } | null>(null);
  /** Where the cursor was when one of those sheets opened, for it to go back to. */
  const cursorAntes = useRef<HTMLElement | null>(null);
  /** «Catálogo de precios», the PDF of what the products sell for (the owner, 2026-10-19). */
  const [catalogo, setCatalogo] = useState(false);
  const puedeEditar = usePuede('editar_productos');
  const puedePrecios = usePuede('editar_precios');
  const verCostos = usePuede('ver_costos');

  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  const precios = useLiveQuery(() => db.precios.toArray(), [], []);
  const listas = useLiveQuery(() => db.listas.toArray(), [], []);
  const existencias = useLiveQuery(() => db.existencias.toArray(), [], []);

  // Every installation needs at least one price list before anything can be priced: by whoever
  // prices, the lists being theirs too (2026-10-13).
  useEffect(() => {
    if (puedePrecios) void asegurarListasBase();
  }, [puedePrecios]);

  const activos = useMemo(() => productos.filter((p) => p.activo), [productos]);
  const inactivos = useMemo(() => productos.filter((p) => !p.activo), [productos]);
  // Everything that matches, in order: the list draws it a part at a time (`cuantos`), and ticking
  // works on all of it. A case's own code finds its product too.
  const resultados = useMemo(
    () => buscarEntre(desactivados ? inactivos : activos, consulta, Infinity, nombre, unidades),
    [desactivados, activos, inactivos, consulta, nombre, unidades],
  );
  /**
   * Each product's presentations, stock and prices, gathered once rather than searched for row by
   * row: the list draws the whole catalogue now, as far down as it is scrolled.
   */
  const unidadesPor = useMemo(() => agrupar(unidades, (u) => u.productoId), [unidades]);
  const existenciasPor = useMemo(() => agrupar(existencias, (e) => e.productoId), [existencias]);
  const precioPor = useMemo(() => new Map(precios.map((x) => [x.id, x.precio])), [precios]);
  /** While ticking, by category: the starter catalogue comes in categories, and so does a shop. */
  const grupos = useMemo(() => {
    if (!eligiendo) return [];
    const porCategoria = new Map<string, Producto[]>();
    for (const p of resultados) {
      const grupo = porCategoria.get(p.categoria);
      if (grupo) grupo.push(p);
      else porCategoria.set(p.categoria, [p]);
    }
    const locale = localeActual();
    return [...porCategoria].sort(([a], [b]) =>
      etiquetaCategoriaProducto(a).localeCompare(etiquetaCategoriaProducto(b), locale),
    );
  }, [eligiendo, resultados]);
  const lista = listaPredeterminada(listas);
  const n = elegidos?.size ?? 0;

  function marcar(ids: readonly string[], marcados: boolean) {
    setElegidos((antes) => {
      const despues = new Set(antes);
      for (const id of ids) {
        if (marcados) despues.add(id);
        else despues.delete(id);
      }
      return despues;
    });
  }

  function cambiarVista(desactivadosPedidos: boolean) {
    setElegidos(null);
    setCuantos(LOTE);
    void navigate({ to: '/productos', search: desactivadosPedidos ? { vista: 'desactivados' } : {}, replace: true });
  }

  async function aplicar() {
    if (!elegidos || elegidos.size === 0) return;
    setAvance(0);
    try {
      await (desactivados ? reactivarProductos : desactivarProductos)([...elegidos], setAvance);
      setElegidos(null);
    } finally {
      setAvance(null);
    }
  }

  function abrir(p: Producto) {
    void navigate({ to: '/productos/$productoId', params: { productoId: p.id } });
  }

  /**
   * What a USB scanner read, wherever the cursor was, as on the other screens that look products up
   * (the owner, 2026-10-03): its product opens, as a tap on its row would, and a code nobody has is
   * asked about in a sheet whose ways out include «Crear producto». Off sale or on, since both lists
   * are this screen's: a can whose product was taken off sale opens that product, to be put back,
   * rather than offering to make it again. Should one of each carry the code, the list on screen
   * wins. While ticking the code goes into the search instead, and its product is found in the
   * list to be ticked: opening it would lose the ticks.
   */
  function leerCodigo(codigo: string) {
    if (eligiendo) {
      setConsulta(codigo);
      return;
    }
    const todos = lecturaDeCodigo(productos, unidades, codigo, { inactivos: true });
    const aLaVista = todos.filter(({ producto }) => producto.activo !== desactivados);
    // Each product once, should two of its presentations carry the code.
    const hallados = [
      ...new Map((aLaVista.length ? aLaVista : todos).map(({ producto }) => [producto.id, producto])).values(),
    ];
    if (hallados.length === 1) {
      abrir(hallados[0]!);
      return;
    }
    cursorAntes.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (hallados.length > 1) setVarios({ codigo, productos: hallados });
    else setPregunta(codigo);
  }
  useLectorUsb(caja, leerCodigo);

  /** One of those sheets let go: the cursor goes back where it was before it opened. */
  function soltar() {
    setPregunta(null);
    setEnsenando(null);
    setVarios(null);
    const antes = cursorAntes.current;
    cursorAntes.current = null;
    if (antes?.isConnected) antes.focus();
  }

  const conVistas = desactivados || inactivos.length > 0;
  const puedeElegir = puedeEditar && !eligiendo && resultados.length > 0;

  return (
    <Pagina
      titulo={t('productos.titulo')}
      subtitulo={t(
        inactivos.length ? 'productos.subtituloConInactivos' : 'productos.subtitulo',
        { activos: activos.length, inactivos: inactivos.length },
      )}
      acciones={
        // No «Semilla» up here since the owner found it unnecessary (2026-10-05): the starter list
        // is offered where it helps, on an empty list and in the first day's steps.
        puedeEditar && (
          <>
            {activos.length > 0 && (
              <button
                type="button"
                className="btn-secundario gap-1.5 px-4 text-sm"
                onClick={() => setCatalogo(true)}
                data-catalogo-abrir
              >
                <Ico icono="precios" className="size-4" />
                {t('productos.catalogo')}
              </button>
            )}
            <Link to="/carga-rapida" className="btn-secundario gap-1.5 px-4 text-sm">
              <Ico icono="rapido" className="size-4" />
              {t('productos.cargaRapida')}
            </Link>
            <Link
              to="/productos/nuevo"
              className="btn-primario gap-1.5 px-4 text-sm"
              data-guia="productos-nuevo"
            >
              <Ico icono="nuevo" className="size-4" />
              {t('comun.nuevo')}
            </Link>
          </>
        )
      }
    >
      <div className="mb-4 flex gap-2">
        <input
          ref={caja}
          className="campo min-w-0 flex-1"
          placeholder={t('productos.buscar')}
          value={consulta}
          onChange={(e) => {
            setConsulta(e.target.value);
            setCuantos(LOTE);
          }}
          autoComplete="off"
          inputMode="search"
        />
        <BotonEscanear onClick={() => setEscaneando(true)} />
      </div>

      {/*
        Scanning here looks things up: each can shows what it is, what it sells for and how many
        there are, without leaving the shelf. It is also where the catalogue learns its codes in
        bulk — walk the aisle, and every can nobody has scanned before asks which product it is.
      */}
      {escaneando && (
        <EscanerProductos
          titulo={t('productos.escanearTitulo')}
          continuo
          onProducto={() => undefined}
          onCerrar={() => setEscaneando(false)}
          describir={(p) => {
            const principal = unidadesDe(unidades, p.id)[0];
            const precio = principal && lista ? precioDe(precios, principal.id, lista.id) : null;
            const stock = totalizar(existencias.filter((e) => e.productoId === p.id));
            return (
              <>
                <p className="tabular">
                  {precio !== null ? formatMoney(precio as Centavos) : t('pos.sinPrecio')} ·{' '}
                  {formatQuantity(stock.cantidad)} {etiquetaUnidad(p.unidadBase, p.unidadBase)}
                </p>
                <Link
                  to="/productos/$productoId"
                  params={{ productoId: p.id }}
                  className="btn-texto mt-1 gap-1 text-slate-200"
                >
                  {t('productos.verProducto')}
                  <Ico icono="adelante" className="size-4" />
                </Link>
              </>
            );
          }}
          desconocido={
            puedeEditar
              ? (codigo) => (
                  <Link to="/productos/nuevo" search={{ codigo }} className="btn-secundario">
                    <Ico icono="nuevo" />
                    {t('productos.crearConCodigo')}
                  </Link>
                )
              : undefined
          }
        />
      )}

      {/*
        The two lists, once something has been taken off sale; and the way into ticking, beside
        them where they leave it room and on a line of its own where they do not (Spanish, on a
        phone), rather than squeezing the tabs until their counts fall under their names.
      */}
      {(conVistas || puedeElegir) && (
        <div className="mb-4 flex flex-wrap items-end gap-2">
          {conVistas && (
            <div className="flex grow border-b border-slate-800">
              {([false, true] as const).map((v) => (
                <button
                  key={String(v)}
                  type="button"
                  onClick={() => cambiarVista(v)}
                  data-sin-icono="pestana"
                  aria-pressed={desactivados === v}
                  className={`-mb-px shrink-0 border-b-2 px-3 py-2 text-sm whitespace-nowrap transition ${
                    desactivados === v
                      ? 'border-marca-500 font-medium text-slate-100'
                      : 'border-transparent text-slate-500 hover:border-slate-500 hover:text-slate-300'
                  }`}
                >
                  {t(v ? 'productos.vistaDesactivados' : 'productos.vistaActivos')}{' '}
                  <span className="tabular ml-1 rounded-full bg-slate-700/60 px-2 py-0.5 text-xs">
                    {(v ? inactivos : activos).length}
                  </span>
                </button>
              ))}
            </div>
          )}
          {puedeElegir && (
            <button
              type="button"
              className="btn-texto ml-auto gap-1.5 text-sm whitespace-nowrap text-slate-300"
              onClick={() => setElegidos(new Set())}
            >
              <Ico icono="seleccionar" className="size-4" />
              {t('productos.seleccionar')}
            </button>
          )}
        </div>
      )}

      {resultados.length === 0 ? (
        desactivados ? (
          <Vacio
            mensaje={t(consulta ? 'productos.ninguno' : 'productos.sinDesactivados')}
            icono="productos"
          />
        ) : (
          <Vacio mensaje={t(consulta ? 'productos.ninguno' : 'productos.sinProductos')} icono="productos">
            {puedeEditar && !consulta && (
              <div className="flex flex-col items-center gap-3">
                <Link to="/productos/semilla" className="btn-primario px-5">
                  <Ico icono="semilla" />
                  {t('productos.empezarCatalogo')}
                </Link>
                <Link to="/carga-rapida" className="btn-texto gap-1.5 text-sm text-slate-400">
                  <Ico icono="rapido" className="size-4" />
                  {t('productos.cargarAMano')}
                </Link>
              </div>
            )}
          </Vacio>
        )
      ) : eligiendo ? (
        <div className="flex flex-col gap-3">
          <Casilla
            className="px-4"
            marcada={resultados.every((p) => elegidos.has(p.id))}
            mixta={resultados.some((p) => elegidos.has(p.id))}
            onCambio={(v) => marcar(resultados.map((p) => p.id), v)}
          >
            <span className="min-w-0 flex-1 font-medium">{t('productos.todos')}</span>
            <span className="tabular text-xs text-slate-500">{resultados.length}</span>
          </Casilla>
          {grupos.map(([categoria, ps]) => {
            const marcados = ps.filter((p) => elegidos.has(p.id)).length;
            return (
              <section key={categoria} className="tarjeta overflow-hidden p-0">
                <Casilla
                  className="border-b border-slate-800 px-4"
                  marcada={marcados === ps.length}
                  mixta={marcados > 0}
                  onCambio={(v) => marcar(ps.map((p) => p.id), v)}
                >
                  <span className="min-w-0 flex-1 truncate font-medium">{etiquetaCategoriaProducto(categoria)}</span>
                  <span className="tabular text-xs text-slate-500">
                    {marcados}/{ps.length}
                  </span>
                </Casilla>
                <ul className="divide-y divide-slate-800/60">
                  {ps.map((p) => (
                    <li key={p.id}>
                      <Casilla className="px-4 py-1.5" marcada={elegidos.has(p.id)} onCambio={(v) => marcar([p.id], v)}>
                        <Miniatura producto={p} tamano="sm" />
                        <span className="min-w-0 flex-1 truncate text-sm">{nombre(p)}</span>
                      </Casilla>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      ) : (
        <>
          <ul className="escalonar flex flex-col gap-2">
            {resultados.slice(0, cuantos).map((p) => {
              const us = unidadesDe(unidadesPor.get(p.id) ?? [], p.id);
              const principal = us[0];
              const precio = principal && lista ? (precioPor.get(precioId(principal.id, lista.id)) ?? null) : null;
              const stock = totalizar(existenciasPor.get(p.id) ?? []);

              return (
                <li key={p.id}>
                  <Link
                    to="/productos/$productoId"
                    params={{ productoId: p.id }}
                    className="tarjeta-accion flex items-center gap-3 p-3 pr-4"
                    data-sin-icono="fila"
                  >
                    <Miniatura producto={p} tamano="lg" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{nombre(p)}</p>
                      <p className="mt-0.5 truncate text-xs text-slate-500">
                        {etiquetaCategoriaProducto(p.categoria)}
                        {p.esMedicamento && ` · ${t('productos.medicamento')}`}
                        {p.perecedero && ` · ${t('productos.perecedero')}`}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="tabular font-semibold">
                        {precio !== null ? formatMoney(precio as Centavos) : '—'}
                      </p>
                      <p
                        className={`tabular mt-0.5 text-xs ${
                          stock.cantidad < 0 ? 'text-rose-400' : 'text-slate-500'
                        }`}
                      >
                        {formatQuantity(stock.cantidad)} {etiquetaUnidad(p.unidadBase, p.unidadBase)}
                        {verCostos && stock.valor > 0 && ` · ${formatMoney(stock.valor)}`}
                      </p>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
          {resultados.length > cuantos && (
            <VerMasAlBajar quedan={resultados.length - cuantos} onMas={() => setCuantos((n) => n + LOTE)} />
          )}
        </>
      )}

      {/*
        What to do with the ticked ones, kept in reach at the bottom of the screen however far
        down the list the ticking went.
      */}
      {eligiendo && (
        <div className="sticky bottom-0 z-10 -mx-5 mt-4 flex flex-col gap-2 border-t border-slate-800 bg-slate-950/95 px-5 py-3 backdrop-blur">
          <div className="flex items-center justify-between gap-3">
            <p role="status" className="text-sm text-slate-300">
              {avance === null
                ? t('productos.elegidos', { n })
                : t(desactivados ? 'productos.reactivando' : 'productos.desactivando', { n: avance, total: n })}
            </p>
            <button
              type="button"
              className="btn-texto gap-1.5 text-sm text-slate-400"
              disabled={avance !== null}
              onClick={() => setElegidos(null)}
            >
              <Ico icono="cancelar" className="size-4" />
              {t('comun.cancelar')}
            </button>
          </div>
          {desactivados ? (
            <button
              type="button"
              className="btn-primario w-full"
              disabled={n === 0 || avance !== null}
              onClick={() => void aplicar()}
            >
              <Ico icono="reiniciar" />
              {t('productos.reactivarVarios', { n })}
            </button>
          ) : (
            <button
              type="button"
              className="btn-peligro w-full"
              disabled={n === 0 || avance !== null}
              onClick={() => setConfirmando(true)}
            >
              <Ico icono="desactivar" />
              {t('productos.desactivarVarios', { n })}
            </button>
          )}
        </div>
      )}

      {confirmando && (
        <Confirmar
          titulo={t('productos.desactivarVariosTitulo', { n })}
          mensaje={t('productos.desactivarVariosMensaje')}
          confirmar={t('preguntar.desactivar')}
          icono="desactivar"
          peligro
          onCerrar={() => setConfirmando(false)}
          onConfirmar={() => void aplicar()}
        />
      )}

      {catalogo && <CatalogoDePrecios onCerrar={() => setCatalogo(false)} />}

      {/* A code scanned that nobody has: the other screens' «Código desconocido», with a new product among its ways out. */}
      {pregunta !== null && (
        <Modal titulo={t('buscador.preguntaTitulo')} onCerrar={soltar}>
          <PreguntaCodigo
            codigo={pregunta}
            puedeEnsenar={puedeEditar}
            conPista={puedeEditar}
            onEnsenar={() => {
              setPregunta(null);
              setEnsenando(pregunta);
            }}
            onSeguir={soltar}
            onElegido={abrir}
          >
            {puedeEditar && (
              <Link to="/productos/nuevo" search={{ codigo: pregunta }} className="btn-secundario">
                <Ico icono="nuevo" />
                {t('productos.crearConCodigo')}
              </Link>
            )}
          </PreguntaCodigo>
        </Modal>
      )}

      {ensenando !== null && (
        <Modal titulo={t('escaner.cualProducto')} onCerrar={soltar}>
          <p className="mb-4 text-sm text-slate-400">{t('escaner.cualProductoAyuda', { codigo: ensenando })}</p>
          <EnsenarCodigo codigo={ensenando} onListo={abrir} />
        </Modal>
      )}

      {varios && (
        <Modal titulo={t('cualEs.titulo')} onCerrar={soltar}>
          <CualEs
            codigo={varios.codigo}
            hallados={varios.productos.map((producto) => ({ producto }))}
            onElegido={({ producto }) => abrir(producto)}
            onSeguir={soltar}
          />
        </Modal>
      )}
    </Pagina>
  );
}

/** Rows by a key of theirs, each group in the order the rows came. */
function agrupar<T>(filas: readonly T[], clave: (fila: T) => string): Map<string, T[]> {
  const grupos = new Map<string, T[]>();
  for (const fila of filas) {
    const grupo = grupos.get(clave(fila));
    if (grupo) grupo.push(fila);
    else grupos.set(clave(fila), [fila]);
  }
  return grupos;
}

/**
 * A row that is its own checkbox: the whole width takes the tap, not a 20px square. `mixta`
 * for a category some of whose products are ticked; tapping it ticks the rest.
 */
function Casilla({
  marcada,
  mixta = false,
  onCambio,
  className = '',
  children,
}: {
  marcada: boolean;
  mixta?: boolean;
  onCambio: (marcada: boolean) => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={`flex min-h-12 cursor-pointer items-center gap-3 ${className}`}>
      <input
        type="checkbox"
        checked={marcada}
        ref={(el) => {
          if (el) el.indeterminate = mixta && !marcada;
        }}
        onChange={(e) => onCambio(e.target.checked)}
        className="size-5 shrink-0 accent-teal-500"
      />
      {children}
    </label>
  );
}
